import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import MapIcon from '@mui/icons-material/Map'
import { Alert, alpha, Box, Button, CircularProgress, IconButton, Typography, useTheme } from '@mui/material'
import { usePostDeal } from 'api/aws/deals/usePostDeal'
import { useProducts } from 'api/aws/useProducts'
import { useActiveUser } from 'api/slack/useActiveUser'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GROBxLoader } from 'theme/components'
import { v4 as uuidv4 } from 'uuid'
import PhaseTimeline from './PhaseTimeline'
import RoadmapCatalog from './RoadmapCatalog'
import { RoadmapProvider, useRoadmap } from './RoadmapContext'
import RoadmapSetup from './RoadmapSetup'
import RoadmapSidebar from './RoadmapSidebar'
import { CatalogItem, getPhaseLabel, LineItem } from './types'

// Inner component that consumes the context
const RoadmapBuilderInner: React.FC<{ onComplete?: (dealPK: string, dealSK: string) => void }> = ({ onComplete }) => {
  const theme = useTheme()
  const { pinned } = useLayoutContext()
  const { state, dispatch } = useRoadmap()
  const { data: products, isLoading } = useProducts()
  // Load catalog from gro_products
  useEffect(() => {
    if (isLoading) {
      dispatch({ type: 'SET_LOADING', loading: true })
      return
    }
    if (products && products.length > 0) {
      // Cast to any[] — gro_products catalog items have extra fields beyond the deal Product type
      const raw = products as any[]
      const catalog: CatalogItem[] = raw
        .filter((p: any) => p.title && (p.points || p.price))
        .map((p: any) => ({
          products_uuid: p.products_uuid || p.id || p.SK || '',
          title: p.title || p.productName || p.name || '',
          category_name: p.category_name || p.category || '',
          subcategory: p.subcategory || '',
          sku: p.sku || '',
          service_line: p.service_line || '',
          points: Number(p.points) || 0,
          price: Number(p.price) || 0,
          margin: Number(p.margin) || 15,
          estimated_hours: Number(p.estimated_hours) || 0,
          delivery_role: p.delivery_role || '',
          ai_enhanced: Boolean(p.ai_enhanced),
          description: p.description || '',
        }))
      dispatch({ type: 'SET_CATALOG', catalog })
    }
  }, [products, isLoading])

  // Loading state
  if (state.isLoading || isLoading) {
    return <GROBxLoader open={state.isLoading || isLoading} message="Syncing Service Catalog" />
  }

  // Empty catalog
  if (state.catalog.length === 0 && !state.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <MapIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          No services in catalog
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          Import services into gro_products to get started.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: 1600,
        mx: 'auto',
        width: '100%',
        paddingTop: pinned ? '60px' : 0,
        transition: 'padding-top 0.3s ease',
      }}
    >
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {state.view !== 'setup' && (
          <IconButton
            size="small"
            onClick={() => dispatch({ type: 'SET_VIEW', view: state.view === 'review' ? 'builder' : 'setup' })}
          >
            <ArrowBackIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
        <MapIcon sx={{ color: 'primary.main', fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Roadmap Builder
        </Typography>
        {state.clientName && state.view !== 'setup' && (
          <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              |
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {state.clientName}
            </Typography>
          </>
        )}
        <Box sx={{ flex: 1 }} />
        {state.view === 'builder' && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {state.catalog.length} services · {state.lineItems.length} selected
          </Typography>
        )}
      </Box>

      {/* View content */}
      {state.view === 'setup' && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <RoadmapSetup />
        </Box>
      )}

      {state.view === 'builder' && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Phase Timeline — full width, pinned at top */}
          <Box
            sx={{
              flexShrink: 0,
              px: 3,
              pt: 1.5,
              pb: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <PhaseTimeline />
          </Box>

          {/* Split layout below timeline */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Left: Catalog scrolls */}
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                overflowY: 'auto',
                px: 3,
                pb: 3,
                pt: 1.5,
                scrollbarGutter: 'stable',
              }}
            >
              <RoadmapCatalog />
            </Box>

            {/* Right: Sidebar scrolls independently */}
            <Box
              sx={{
                width: 360,
                flexShrink: 0,
                borderLeft: '1px solid',
                borderColor: 'divider',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <RoadmapSidebar />
            </Box>
          </Box>
        </Box>
      )}

      {state.view === 'review' && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
          <RoadmapReview onComplete={onComplete} />
        </Box>
      )}
    </Box>
  )
}

// Review view — summary before creating deal
const RoadmapReview: React.FC<{ onComplete?: (dealPK: string, dealSK: string) => void }> = ({ onComplete }) => {
  const theme = useTheme()
  const { state, dispatch, phaseSummaries, ratePerPoint, effectiveRate, totalPoints, totalInvestment } = useRoadmap()
  const { activeUser } = useActiveUser()
  const { mutateAsync: postDeal, isPending: isCreating } = usePostDeal()
  const navigate = useNavigate()
  const [error, setError] = useState('')
  const [createdDealPath, setCreatedDealPath] = useState('')

  const handleCreateDeal = async () => {
    setError('')

    // Require an org to create a deal
    if (!state.clientOrgId) {
      setError('Select an existing organization from the Client / Company dropdown to create a deal.')
      return
    }

    const currentUser = activeUser?.user || { id: 'unknown', name: 'Unknown' }
    const PK = `ORG_${state.clientOrgId}`

    // Compute dates from engagement config
    const startDate = state.engagementConfig.startMonth
      ? `${state.engagementConfig.startMonth}-01`
      : new Date().toISOString().slice(0, 10)
    const startD = new Date(startDate)
    const endD = new Date(startD)
    endD.setMonth(endD.getMonth() + state.engagementConfig.duration)
    endD.setDate(endD.getDate() - 1) // last day of final month (inclusive)
    const endDate = endD.toISOString().slice(0, 10)

    // Setup end date — setup items are billed once in the first month only
    const setupEndD = new Date(startD)
    setupEndD.setMonth(setupEndD.getMonth() + 1)
    setupEndD.setDate(setupEndD.getDate() - 1) // last day of first month (inclusive)
    const setupEndDate = setupEndD.toISOString().slice(0, 10)

    // Consolidate line items by products_uuid — 1 product row per unique service
    const setupItems = state.lineItems.filter(li => li.phase === 'setup')
    const monthlyItems = state.lineItems.filter(li => li.phase !== 'setup')

    // Group monthly items by products_uuid so the same service across months becomes 1 row
    const monthlyGroups = new Map<string, LineItem[]>()
    for (const li of monthlyItems) {
      const key = li.products_uuid || li.lineItemId
      const group = monthlyGroups.get(key) || []
      group.push(li)
      monthlyGroups.set(key, group)
    }

    type ConsolidatedItem = LineItem & {
      _isSetup?: boolean
      _description?: string
      _monthCount?: number
      _firstMonth?: number
    }
    const consolidatedProducts: ConsolidatedItem[] = []

    // Setup items: billed once (qty=1, 1-month date range)
    for (const li of setupItems) {
      consolidatedProducts.push({ ...li, _isSetup: true, _description: `Setup — ${li.title}` })
    }

    // Monthly items: consolidate into 1 row, qty=1, full date range (schedulers iterate monthly)
    for (const [, group] of monthlyGroups) {
      const representative = group[0]
      const months = group.map(li => parseInt(li.phase.replace('month-', ''), 10))
      months.sort((a, b) => a - b)
      const phaseRange =
        months.length === 1 ? getPhaseLabel(group[0].phase) : `Mo ${months[0]}-${months[months.length - 1]}`

      consolidatedProducts.push({
        ...representative,
        quantity: 1, // qty=1; the date range determines billing months
        _isSetup: false,
        _description: `${phaseRange} — ${representative.title}`,
        _monthCount: months.length,
        _firstMonth: months[0],
      })
    }

    // Calculate setup total from actual setup items
    const setupTotal = setupItems.reduce((sum, li) => sum + li.points * effectiveRate * li.quantity, 0)

    const products = consolidatedProducts.map((li, idx) => {
      const instanceId = uuidv4()
      const isSetup = li._isSetup
      const unitPrice = li.points * effectiveRate
      const margin = li.margin ?? 15
      // All money fields are PER-MONTH values — the date range determines billing duration.
      // Backend + deriveProductMoney both multiply price × months, so storing totals here would double-count.
      const monthlyGP = unitPrice * (margin / 100) // gross profit per month
      // Compute date range from the actual months this service spans
      let productStartDate = startDate
      let productEndDate = isSetup ? setupEndDate : endDate
      if (!isSetup && li._monthCount && li._monthCount > 0 && li._firstMonth) {
        const firstMonth = li._firstMonth
        // Start date: offset from engagement start by (firstMonth - 1) months
        const pStart = new Date(startDate)
        pStart.setMonth(pStart.getMonth() + (firstMonth - 1))
        productStartDate = pStart.toISOString().slice(0, 10)
        // End date: start + monthCount months, minus 1 day (last day of final month)
        const pEnd = new Date(pStart)
        pEnd.setMonth(pEnd.getMonth() + li._monthCount)
        pEnd.setDate(pEnd.getDate() - 1)
        productEndDate = pEnd.toISOString().slice(0, 10)
      }
      return {
        PK,
        SK: `PRODUCT_${instanceId}`,
        EntityType: 'product',
        instanceId,
        rowId: instanceId,
        productName: li.title,
        category: li.category,
        category_name: li.category_name || li.category,
        subcategory: li.subcategory || '',
        price: unitPrice,
        billedPrice: unitPrice,
        qty: li.quantity,
        margin,
        total: unitPrice * li.quantity,
        netTotal: unitPrice * li.quantity,
        grossProfit: monthlyGP.toFixed(2),
        phase: li.phase,
        points: li.points,
        ratePerPoint: effectiveRate,
        sku: li.sku || '',
        service_line: li.service_line || '',
        estimated_hours: li.estimated_hours || 0,
        delivery_role: li.delivery_role || '',
        description: li._description || `${getPhaseLabel(li.phase)} — ${li.title}`,
        startDate: productStartDate,
        endDate: productEndDate,
        dateRange: [productStartDate, productEndDate],
        status: 'Active',
        sortOrder: idx,
        index: String(idx),
        isNew: true,
        mediaSpend: 0,
        accountUsed: '',
        addtlDescriptor: '',
        payer: '',
      }
    })

    // Monthly total excludes setup (setup is a one-time upfront charge)
    const recurringTotal = totalInvestment - setupTotal
    const monthlyTotal = Math.round(recurringTotal / state.engagementConfig.duration)

    // Compute gross profit from actual product margins (not a hardcoded %)
    const monthlyGrossProfit = products
      .filter(p => p.phase !== 'setup')
      .reduce((sum, p) => sum + p.price * (p.margin / 100), 0)
    const setupGrossProfit = products
      .filter(p => p.phase === 'setup')
      .reduce((sum, p) => sum + parseFloat(String(p.grossProfit)), 0)
    const totalGrossProfit = monthlyGrossProfit * state.engagementConfig.duration + setupGrossProfit

    const dealData: any = {
      name: `${state.clientName} — ${state.engagementConfig.duration}mo Roadmap`,
      organizations: [state.clientOrgId],
      type: 'MSA',
      billingModel: 'points' as const,
      ratePerPoint: effectiveRate,
      stage: 'Proposal',
      status: 'Draft',
      dealStatus: 'Active',
      startDate,
      endDate,
      owner: [currentUser.id],
      createdBy: [currentUser.id],
      isDraft: true,
      needsApproval: false,
      isRequested: false,
      isApproved: false,
      isInternal: false,
      isCompleted: false,
      isCreated: false,
      noSignature: false,
      total: totalInvestment,
      totalPoints,
      monthlyTotal,
      setupFee: setupTotal,
      implementationFee: 0,
      grossProfit: Math.round(totalGrossProfit * 100) / 100,
      mediaSpend: 0,
      numberOfPayments: state.engagementConfig.duration,
      onboardingStatus: 'Not Started',
      description: state.salesNotes || '',
      contacts: [],
      primaryContact: [],
      // Pre-compute totals so DealInfoCard can display them immediately
      totals: {
        monthlyTotal,
        setupTotal,
        implementationFee: 0,
        grossProfit: Math.round(monthlyGrossProfit * 100) / 100,
        total: totalInvestment,
        totalGrossProfit: Math.round(totalGrossProfit * 100) / 100,
        spend: 0,
      },
    }

    try {
      const result = await postDeal({ dealData, products, currentUser })
      if (result?.deal) {
        const dealPK = result.deal.PK
        const dealSK = result.deal.SK
        setCreatedDealPath(`/deals/${dealPK}/${dealSK}/details`)
        onComplete?.(dealPK, dealSK)
      }
    } catch (err) {
      setError(err.message || 'Failed to create deal')
    }
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Roadmap Review
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
        {state.clientName} · {state.engagementConfig.duration}-month engagement · {totalPoints} points · $
        {totalInvestment.toLocaleString()}
      </Typography>

      {/* Phase breakdown table */}
      <Box
        component="table"
        sx={{
          width: '100%',
          borderCollapse: 'collapse',
          mb: 3,
          '& th, & td': {
            p: 1,
            textAlign: 'left',
            borderBottom: '1px solid',
            borderColor: 'divider',
            fontSize: 13,
          },
          '& th': {
            fontWeight: 700,
            color: 'text.secondary',
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          },
        }}
      >
        <thead>
          <tr>
            <th>Phase</th>
            <th>Items</th>
            <th>Strategic</th>
            <th>Executable</th>
            <th style={{ textAlign: 'right' }}>Points</th>
            <th style={{ textAlign: 'right' }}>Budget</th>
            <th style={{ textAlign: 'right' }}>Utilization</th>
          </tr>
        </thead>
        <tbody>
          {phaseSummaries.map(ps => (
            <tr key={ps.phase}>
              <td style={{ fontWeight: 600 }}>{ps.label}</td>
              <td>{ps.itemCount}</td>
              <td>{ps.strategicCount}</td>
              <td>{ps.executableCount}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ps.usedPoints}</td>
              <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{ps.budget}</td>
              <td
                style={{
                  textAlign: 'right',
                  fontWeight: 600,
                  color: ps.isOverBudget
                    ? theme.palette.text.error
                    : ps.utilization >= 85
                    ? theme.palette.text.complete
                    : undefined,
                }}
              >
                {ps.utilization}%
              </td>
            </tr>
          ))}
        </tbody>
      </Box>

      {/* Items by phase */}
      {phaseSummaries
        .filter(ps => ps.itemCount > 0)
        .map(ps => {
          const items = state.lineItems.filter(li => li.phase === ps.phase)
          return (
            <Box key={ps.phase} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                {ps.label}
              </Typography>
              {items.map(li => (
                <Box key={li.lineItemId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                  <Typography variant="caption">
                    {li.title}
                    {li.quantity > 1 && <> x{li.quantity}</>}
                  </Typography>
                  <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    {li.points * li.quantity} pts
                  </Typography>
                </Box>
              ))}
            </Box>
          )
        })}

      {/* Summary box */}
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'primary.main',
          bgcolor: alpha(theme.palette.text.highlight, 0.06),
          mt: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Total Points</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {totalPoints}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2">Rate</Typography>
          <Typography variant="body2">${effectiveRate}/pt</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Total Investment
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
            ${totalInvestment.toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {createdDealPath ? (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon />}
          sx={{ mt: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate(createdDealPath)}>
              View Deal
            </Button>
          }
        >
          Deal created successfully!
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => dispatch({ type: 'SET_VIEW', view: 'builder' })}>
            Back to Builder
          </Button>
          <Button variant="contained" sx={{ flex: 1 }} disabled={isCreating} onClick={handleCreateDeal}>
            {isCreating ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                Creating Deal...
              </>
            ) : (
              'Create Deal from Roadmap'
            )}
          </Button>
        </Box>
      )}
    </Box>
  )
}

interface RoadmapBuilderProps {
  initialClientName?: string
  initialClientOrgId?: string
  initialClientIndustry?: string
  onComplete?: (dealPK: string, dealSK: string) => void
}

// Wrapper with provider
const RoadmapBuilder: React.FC<RoadmapBuilderProps> = ({
  initialClientName,
  initialClientOrgId,
  initialClientIndustry,
  onComplete,
}) => {
  return (
    <RoadmapProvider
      initialClientName={initialClientName}
      initialClientOrgId={initialClientOrgId}
      initialClientIndustry={initialClientIndustry}
    >
      <RoadmapBuilderInner onComplete={onComplete} />
    </RoadmapProvider>
  )
}

export default RoadmapBuilder
